import { Fragment, type ReactNode } from 'react';

/*
 * Rich inline markup for LLM-composed CARD text (conversation-note sections, conflict cards). The
 * backend LLM may emit a CONSTRAINED subset — **bold** for emphasis, *italic* for a short aside, a
 * `conv:<id>` link, and (note mode) `code` spans. This parser turns that into React nodes.
 *
 * SECURITY / ROBUSTNESS is the whole point: an LLM string is untrusted, so
 *  - we NEVER set innerHTML — every token becomes a React element and all text is React-escaped;
 *  - a link's ref is resolved against a fixed whitelist (`conv:<id>` where id matches a strict
 *    pattern). ANY other ref — a `javascript:`/`http:` scheme, a malformed or missing id — degrades
 *    to the plain visible label (the href is dropped). A bad LLM link can never navigate anywhere
 *    unexpected, inject HTML, or break the render.
 *
 * (The band-prose block parser — parseRichBlocks — and the `#band` anchor token died with the
 * bandNarratives feature in the 2026-07-26 mock-alignment pass: LLM text now lives only on cards.)
 */

// Conversation ids are opaque tokens (uuids / prefixed slugs); keep the pattern strict so a stray
// ref can't smuggle anything into the onNav path.
const CONV_ID = /^[A-Za-z0-9_-]{6,64}$/;

// One regex, three alternatives (tried left-to-right at each position): a [label](ref) link, a **bold**
// span, or an *italic* span. Everything between matches is literal text. `[^\]]+` / `[^)]+` / `[^*]+` /
// `[^*\n]+` keep each token from swallowing past its delimiter. Bold precedes italic so `**x**` is one
// bold token, not two stray italics. The CODE variant adds an opt-in `code` alternative for note bullets
// (conversation-note viewer); other callers keep the original regex so backticks stay literal there.
const TOKEN_RE = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*/g;
const TOKEN_RE_CODE = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`\n]+)`/g;

export type RichToken =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'codeSpan'; text: string }
  | { kind: 'conv'; text: string; id: string };

export interface RichTextOpts { codeSpans?: boolean }

export function parseRichText(input: string | null | undefined, opts?: RichTextOpts): RichToken[] {
  const tokens: RichToken[] = [];
  if (!input) return tokens;
  const re = opts?.codeSpans ? TOKEN_RE_CODE : TOKEN_RE;
  let last = 0;
  let m: RegExpExecArray | null;
  re.lastIndex = 0;
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) tokens.push({ kind: 'text', text: input.slice(last, m.index) });
    if (m[3] !== undefined) {
      tokens.push({ kind: 'bold', text: m[3] });
    } else if (m[4] !== undefined) {
      tokens.push({ kind: 'italic', text: m[4] });
    } else if (m[5] !== undefined) {
      tokens.push({ kind: 'codeSpan', text: m[5] });
    } else {
      tokens.push(resolveLink(m[1], m[2].trim()));
    }
    last = m.index + m[0].length;
  }
  if (last < input.length) tokens.push({ kind: 'text', text: input.slice(last) });
  return tokens;
}

// Resolve a link ref against the whitelist; anything unrecognized becomes plain text (label only).
function resolveLink(label: string, ref: string): RichToken {
  if (ref.toLowerCase().startsWith('conv:')) {
    const id = ref.slice(5).trim();
    if (CONV_ID.test(id)) return { kind: 'conv', text: label, id };
  }
  return { kind: 'text', text: label };
}

// Render tokens to React nodes. bold/italic RECURSE into their inner text so the LLM can emphasize a
// link: `**[label](conv:id)**` becomes a bold accent link rather than literal markup. (The model wraps
// the key item's link in bold; without recursion the bold span swallows the link as plain text — the
// `[...](...)` showed raw on screen.) Recursion terminates: a bold/italic span's inner text never
// contains `*` (the delimiters exclude it), so no further bold/italic can nest.
function renderTokens(
  tokens: RichToken[],
  onNav: (id: string) => void,
  opts?: RichTextOpts,
): ReactNode[] {
  return tokens.map((tk, i) => {
    if (tk.kind === 'bold')
      return <b key={i}>{renderTokens(parseRichText(tk.text, opts), onNav, opts)}</b>;
    if (tk.kind === 'italic')
      return <i key={i}>{renderTokens(parseRichText(tk.text, opts), onNav, opts)}</i>;
    if (tk.kind === 'codeSpan')
      return <code key={i}>{tk.text}</code>;
    if (tk.kind === 'conv')
      return (
        <button key={i} type="button" className="brief-band-link" onClick={() => onNav(`conversations/${tk.id}`)}>
          {tk.text}
        </button>
      );
    return <Fragment key={i}>{tk.text}</Fragment>;
  });
}

/** Render a card string with its inline markup as safe React nodes. `onNav` opens a conversation.
 *  Plain strings (legacy rows, no markup) render verbatim. Note mode (conversation-note viewer):
 *  pass `codeSpans` to render backtick spans as <code>. */
export function RichText({
  text,
  onNav,
  codeSpans,
}: {
  text: string | null | undefined;
  onNav: (id: string) => void;
  codeSpans?: boolean;
}): ReactNode {
  const opts = codeSpans ? { codeSpans } : undefined;
  return <>{renderTokens(parseRichText(text, opts), onNav, opts)}</>;
}
